(ns rothko.editor.manatee.actions
  "Contains a keymap for Manatee which translates into rothko actions"
  (:require [clojure.string :as string]
            [rothko.editor.actions :as actions]
            [rothko.editor.behaviours.backspace :as backspace-behaviour]
            [rothko.editor.behaviours.delete :as delete-behaviour]
            [rothko.editor.behaviours.double-click :as double-click-behaviour]
            [rothko.editor.behaviours.insert :as insert-behaviour]
            [rothko.editor.behaviours.mark :as mark-behaviour]
            [rothko.editor.behaviours.space :as space-behaviour]
            [rothko.editor.behaviours.tag :as tag-behaviour]
            [rothko.editor.document.history.linear :as history-transform]
            [rothko.editor.document.navigators :as document-navigators]
            [rothko.editor.document.transform.compound :as compound-transform]
            [rothko.editor.manatee.markers :as markers]
            [rothko.editor.matcher :as m]))

;; ctrl + alt + char often produces alt chars in windows, and so one needs to be
;; aware of this before adding a shortcut and handle it, see i and u for examples.
;; Perhaps it would be worth choosing different modifier combos for future shortcuts.

(def tags
  {:hesitation
   {:name :hesitation
    :representation "%HESITATION%"}

   :speech-unit-statement
   {:name :speech-unit-statement
    :representation "/."}

   :speech-unit-question
   {:name :speech-unit-question
    :representation "/?"}

   :speech-unit-incomplete
   {:name :speech-unit-incomplete
    :representation "/-"}

   :code-switch
   {:name :code-switch
    :representation "ƒ"}

   :foreign-proper-noun
   {:name :foreign-proper-noun
    :representation "Ƥ"}

   :bc-backchannel
   {:name :bc-backchannel
    :representation "BC"}

   :unknown
   {:name :unknown
    :representation "‽"}})

(defn transform-options
  [implementation]
  {:mark-spaces-as-edited? (= implementation :script-annotator)
   :space-before-replacement? (not= implementation :script-annotator)
   :remove-dangling-spaces? (not= implementation :script-annotator)
   :space-edits? (= implementation :script-annotator)
   :keep-system-flag? (= implementation :script-annotator)
   :insert-before-word? (= implementation :script-annotator)
   :allow-overwriting-annotation? (= implementation :script-annotator)})

(defn insert-tag
  [implementation tag-name]
  (-> (transform-options implementation)
      (assoc :tag (get tags tag-name))
      (tag-behaviour/transform)
      (history-transform/append)))

(defn transcript-editor-insert
  [implementation name]
  (let [name (string/upper-case name)]
    (history-transform/append
     (insert-behaviour/transform (-> (transform-options implementation)
                                     (assoc :name name))))))

(defmethod actions/->transformation
  {::actions/key {:key #{"z"}
                  :ctrl true
                  :alt false}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator :script-annotator :mwe-annotator}}
  [_]
  (history-transform/navigate :undo))

(defmethod actions/->transformation
  {::actions/key {:key #{"y"}
                  :ctrl true
                  :alt false}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator :script-annotator :mwe-annotator}}
  [_]
  (history-transform/navigate :redo))

(defmethod actions/->transformation
  {::actions/key {:key #{"u"}
                  :ctrl true
                  :alt true}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator :script-annotator}}
  [{::actions/keys [implementation]}]
  (insert-tag implementation :unknown))

(defmethod actions/->transformation
  ;; ctrl + alt + u on windows (UK) produces ú, with alt keys on Windows 10 and without in Windows 11
  {::actions/key {:key #{"ú"}}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator :script-annotator}}
  [{::actions/keys [implementation]}]
  (insert-tag implementation :unknown))

(defmethod actions/->transformation
  {::actions/key {:key #{"f"}
                  :ctrl true
                  :alt true}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [{::actions/keys [implementation]}]
  (insert-tag implementation :foreign-proper-noun))

(defmethod actions/->transformation
  {::actions/key {:key #{"s"}
                  :ctrl true
                  :alt true}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [{::actions/keys [implementation]}]
  (insert-tag implementation :speech-unit-statement))

(defmethod actions/->transformation
  {::actions/key {:key #{"y"}
                  :ctrl true
                  :alt true}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [{::actions/keys [implementation]}]
  (insert-tag implementation :speech-unit-question))

(defmethod actions/->transformation
  {::actions/key {:key #{"i"}
                  :ctrl true
                  :alt true}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [{::actions/keys [implementation]}]
  (insert-tag implementation :speech-unit-incomplete))

(defmethod actions/->transformation
  ;; ctrl + alt + i on windows (UK) produces í, with alt keys on Windows 10 and without in Windows 11
  {::actions/key {:key #{"í"}}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [{::actions/keys [implementation]}]
  (insert-tag implementation :speech-unit-incomplete))

(defmethod actions/->transformation
  {::actions/key {:key #{"r"}
                  :ctrl true
                  :alt true}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [_]
  (history-transform/append
   compound-transform/remove-all-tags-and-marks))

(defmethod actions/->transformation
  {::actions/key {:key #{"r"}
                  :ctrl true
                  :alt true}
   ::actions/type :key
   ::actions/implementation :script-annotator}
  [_]
  (history-transform/append
   compound-transform/remove-changes))

(defmethod actions/->transformation
  {::actions/key {:key #{"h"}
                  :ctrl true}
   ::actions/type :key
   ::actions/implementation :est-transcription-annotator}
  [_]
  (insert-tag :est-transcription-annotator :hesitation))

(defmethod actions/->transformation
  {::actions/key {:key #{"c"}
                  :ctrl true
                  :alt true}
   ::actions/type :key
   ::actions/implementation :est-transcription-annotator}
  [_]
  (insert-tag :est-transcription-annotator :code-switch))

(def letter-or-number
  #"[A-Za-z0-9]")

(defmethod actions/->transformation
  {::actions/key {:key letter-or-number
                  :ctrl false
                  :alt false}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [{::actions/keys [implementation]
    {:keys [key]} ::actions/key}]
  (transcript-editor-insert implementation key))

(defmethod actions/->transformation
  {::actions/key {:key #{"'" "-"}}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [{::actions/keys [implementation]
    {:keys [key]} ::actions/key}]
  (transcript-editor-insert implementation key))

(def punctuation
  #{"!" "\"" "#" "$" "£" "%" "&" "'" "(" ")" "*" "+" "," "\\" "-" "." "/" ":" ";"
    "<" "=" ">" "?" "@" "[" "]" "^" "_" "`" "{" "|" "}" "~"})

(defmethod actions/->transformation
  {::actions/key {:key (m/one-of letter-or-number punctuation)
                  :ctrl false
                  :alt false
                  :meta false}
   ::actions/type :key
   ::actions/implementation :script-annotator}
  [{{:keys [key]} ::actions/key}]
  (history-transform/append
   (insert-behaviour/transform (-> (transform-options :script-annotator)
                                   (assoc :name key
                                          :punctuation? (contains? punctuation key))))))

(defmethod actions/->transformation
  {::actions/key {:key " "}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator :script-annotator}}
  [{::actions/keys [implementation]}]
  (history-transform/append
   (space-behaviour/transform
    (transform-options implementation))))

(defmethod actions/->transformation
  {::actions/key {:key "Enter"}
   ::actions/type :key
   ::actions/implementation :script-annotator}
  [_]
  (history-transform/append
   (space-behaviour/transform (-> (transform-options :script-annotator)
                                  (assoc :chars "\n")))))

(defmethod actions/->transformation
  {::actions/key {:key #{"Enter" "Tab"}}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [_]
  actions/no-op)

(defmethod actions/->transformation
  {::actions/key {:key "Backspace"}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator :script-annotator}}
  [{::actions/keys [implementation]}]
  (history-transform/append
   (backspace-behaviour/transform
    (transform-options implementation))))

(defmethod actions/->transformation
  {::actions/key {:key "Delete"}
   ::actions/type :key
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator :script-annotator}}
  [{::actions/keys [implementation]}]
  (history-transform/append
   (delete-behaviour/transform
    (transform-options implementation))))

(defmethod actions/->transformation
  {::actions/key {:key #{"Backspace" "Delete"}}
   ::actions/type :key
   ::actions/implementation :mwe-annotator}
  [_]
  (history-transform/append
   compound-transform/remove-all-mwe-tags-and-marks))

(defmethod actions/->transformation
  {::actions/button {:name (set (keys tags))}
   ::actions/type :button
   ::actions/implementation :est-transcription-annotator}
  [{{:keys [name]} ::actions/button}]
  (insert-tag :est-transcription-annotator name))

(defmethod actions/->transformation
  {::actions/button {:name #{:speech-unit-statement
                             :speech-unit-question
                             :speech-unit-incomplete
                             :unknown
                             :foreign-proper-noun}}
   ::actions/type :button
   ::actions/implementation :est-error-annotator}
  [{{:keys [name]} ::actions/button}]
  (insert-tag :est-error-annotator name))

(defmethod actions/->transformation
  {::actions/button {:name :unknown}
   ::actions/type :button
   ::actions/implementation :script-annotator}
  [_]
  (insert-tag :script-annotator :unknown))

(defmethod actions/->transformation
  {::actions/button {:name :mwe}
   ::actions/type :button
   ::actions/implementation #{:mwe-annotator :script-annotator}}
  [_]
  (history-transform/append
   tag-behaviour/mwe-transform))

(defmethod actions/->transformation
  {::actions/button {:name (m/fn-matcher markers/match-markers)}
   ::actions/type :button}
  [{{:keys [name]} ::actions/button}]
  (history-transform/append
   (mark-behaviour/transform name {name {:name name}})))

(defmethod actions/->transformation
  {::actions/button {:name :spelling}
   ::actions/type :button
   ::actions/implementation :est-error-annotator}
  [{{:keys [name]} ::actions/button}]
  (history-transform/append
   (mark-behaviour/transform
    name
    {:spelling {:name :spelling}})))

(defmethod actions/->transformation
  {::actions/button {:name :discontinuity}
   ::actions/type :button
   ::actions/implementation :mwe-annotator}
  [{{:keys [name]} ::actions/button}]
  (history-transform/append
   (mark-behaviour/transform
    name
    {:discontinuity {:name :discontinuity}})))

(defmethod actions/->transformation
  {::actions/button {:name :delete-marks}
   ::actions/type :button
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator}}
  [_]
  (history-transform/append
   compound-transform/remove-all-tags-and-marks))

(defmethod actions/->transformation
  {::actions/button {:name :delete-marks}
   ::actions/type :button
   ::actions/implementation :mwe-annotator}
  [_]
  (history-transform/append
   compound-transform/remove-all-mwe-tags-and-marks))

(defmethod actions/->transformation
  {::actions/button {:name :remove-changes}
   ::actions/type :button
   ::actions/implementation :script-annotator}
  [_]
  (history-transform/append
   compound-transform/remove-changes))

(defmethod actions/->transformation
  {::actions/button {:name #{:undo :redo}}
   ::actions/type :button
   ::actions/implementation #{:est-transcription-annotator :est-error-annotator :script-annotator :mwe-annotator}}
  [{{:keys [name]} ::actions/button}]
  (history-transform/navigate name))

(defmethod actions/->transformation
  {::actions/key {:key #{"x"}
                  :ctrl true
                  :alt false}
   ::actions/type :key
   ::actions/implementation #{:script-annotator :mwe-annotator}}
  [_]
  document-navigators/log-selected-tokens)

(defmethod actions/->transformation
  {::actions/type :double-click
   ::actions/implementation :script-annotator}
  [_]
  double-click-behaviour/transform)

(defmethod actions/->transformation
  {::actions/button {:name :split-segment}
   ::actions/type :button
   ::actions/implementation :est-transcription-annotator}
  [{::actions/keys [effect-fn]}]
  (document-navigators/effect effect-fn))
